import { cn } from '@/lib/cn';

/**
 * Card — superficie en reposo: PLANA, con filete de 1 px.
 *
 * La decisión viene medida del 2026-09-01: había 21 sombras en reposo sobre un
 * fondo que ya era gris. Con doce tarjetas en pantalla, doce halos apilados dan
 * un aire sucio y ninguna jerarquía — si todo flota, nada flota. La sombra
 * quedó para lo que de verdad está por encima del plano: panel, menú, modal,
 * aviso.
 *
 * De regalo, el filete funciona en los dos temas con la misma regla: una sombra
 * negra sobre #0f172a no se ve; un borde #334155 sí.
 *
 * `interactiva` SÍ recupera la sombra al pasar por encima, y es coherente: solo
 * hay una tarjeta bajo el puntero a la vez, así que no se apilan, y es la señal
 * de que se puede pulsar.
 *
 * Y no anides tarjetas. Si algo dentro necesita agruparse, un filete horizontal
 * lo separa sin construir otra caja.
 */
export function Card({
  interactiva, className, children, ...props
}: React.HTMLAttributes<HTMLDivElement> & { interactiva?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-md border border-line bg-surface',
        interactiva &&
          'transition-shadow duration-200 hover:shadow-raised focus-within:shadow-raised',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** Cabecera de tarjeta. El título va a `--fs-3`/600 en navy y el metadato a
 *  `--fs-2`/400 en gris: dos señales —tamaño y color— en lugar de una. Antes
 *  los dos medían lo mismo y la jerarquía colgaba solo del grosor de la letra,
 *  que es la señal más débil que existe. */
export function CardHeader({
  titulo, meta, accion, className,
}: {
  titulo: React.ReactNode;
  meta?: React.ReactNode;
  accion?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start gap-3 border-b border-line px-4 py-3', className)}>
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-3 font-semibold text-brand-ink">{titulo}</h3>
        {meta && <p className="mt-0.5 text-2 text-muted">{meta}</p>}
      </div>
      {accion && <div className="shrink-0">{accion}</div>}
    </div>
  );
}

export function CardBody({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('p-4', className)}>{children}</div>;
}
